<template>
	<div v-if="!showForgotPassword" class="social-login__form">
		<c-input
			id="email"
			v-model="authResult.UserEmail"
			:placeholder="authResult.UserEmail"
			disabled
			type="text"
			class="email-input mb-2"
		/>
		<c-validated-input
			ref="passwordRef"
			v-model="password"
			vid="password"
			placeholder="Password"
			name="Password"
			autocomplete="on"
			rules="required"
			type="password"
			role="textbox"
			@keydown.enter.native="handleClick"
		/>
		<c-callout v-if="passwordError" type="error">
			<p>Incorrect password.</p>
		</c-callout>
		<c-checkbox
			id="remember-me"
			v-model="rememberMe"
			label="Remember Me"
		/>
		<c-status-button
			class="button button--primary sz-4 bg-deep"
			title="Log In"
			:disabled="disableSubmit"
			:status="loginStatus"
			@click.native="handleClick()"
		>
			Log In
		</c-status-button>
		<button class="button--no-style text-center block underline w-100" @click="handleForgotPassword">
			Forgot password?
		</button>
	</div>
	<div v-else class="social-login__form">
		<c-callout id="reset-sent" type="info">
			We're emailing you a password reset link now.
		</c-callout>

		<c-input
			id="email"
			v-model="authResult.UserEmail"
			:placeholder="authResult.UserEmail"
			disabled
			type="text"
			class="email-input mb-2"
		/>
		<button class="button--no-style text-center block underline w-100" @click="handleResetLogin">
			Return to log in.
		</button>
	</div>
</template>
<script lang="ts">
	import CValidatedInput from '@cognitoforms/shared-components/src/components/form-controls/ValidatedInput.vue';
	import CCheckbox from '@cognitoforms/shared-components/src/components/form-controls/Checkbox.vue';
	import CInput from '@cognitoforms/shared-components/src/components/form-controls/Input.vue';
	import type { ButtonStatus } from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CStatusButton from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CCallout from '@cognitoforms/shared-components/src/components/content/Callout.vue';
	import type { PropType } from 'vue';
	import { computed, defineComponent, onMounted, ref } from 'vue';
	import type { PasswordLoginResult } from '@cognitoforms/types/server-types/services/infrastructure/authentication/password-login-result';

	export default defineComponent({
		name: 'CPasswordLogin',
		components: {
			CValidatedInput,
			CInput,
			CCheckbox,
			CStatusButton,
			CCallout
		},
		props: {
			authResult: {
				type: Object as PropType<PasswordLoginResult>,
				default: () => {}
			},
			loginStatus: {
				type: String as PropType<ButtonStatus>,
				default: 'default'
			}
		},
		emits: ['password-login', 'reset-login', 'forgot-password'],
		setup(props, { emit }) {
			const password = ref<string>('');
			const rememberMe = ref<boolean>(false);
			const showForgotPassword = ref<boolean>(false);

			const passwordError = computed<boolean>(() => {
				return props.authResult.PasswordError;
			});

			const disableSubmit = computed<boolean>(() => {
				return password.value === '';
			});

			const handleClick = () => {
				emit('password-login', { password: password.value, rememberMe: rememberMe.value });
			};

			const handleForgotPassword = () => {
				showForgotPassword.value = true;
				emit('forgot-password');
			};

			const handleResetLogin = () => {
				emit('reset-login');
			};

			// Autofocus password input on load
			const passwordRef = ref<InstanceType<typeof CValidatedInput>>();
			onMounted(() => {
				passwordRef.value.$el.querySelector('input').focus();
			});

			return {
				password,
				passwordRef,
				passwordError,
				rememberMe,
				disableSubmit,
				showForgotPassword,
				handleClick,
				handleForgotPassword,
				handleResetLogin
			};
		}
	});
</script>

<style lang="scss">
.checkbox {

	&__text {

		& > * {
			display: inline-block;
			margin-left: 3px;
		}
	}
}
</style>